import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, Eyebrow, Reveal, fadeUp, staggerContainer } from "@/components/site/motion";

const leaders = [
  {
    name: "Anshu Soni",
    role: "Director",
    photo: "/team/anshu-soni.png",
    bio: "Anshu Soni serves as a Director of Bytevora IT Solutions Private Limited and is involved in the company’s strategic direction, business development, and long-term growth.",
  },
  {
    name: "Dayaram",
    role: "Director",
    photo: "/team/dayaram.png",
    bio: "Dayaram serves as a Director of Bytevora IT Solutions Private Limited and contributes to the company’s governance, operational direction, and long-term development.",
  },
];

const workPrinciples = [
  { title: "Clear Ownership", desc: "Every project should have clear responsibility, communication, and accountability." },
  { title: "Practical Collaboration", desc: "Engineers, designers, and decision-makers should work around the same problem rather than in isolated silos." },
  { title: "Technical Discipline", desc: "We value maintainable systems, thoughtful architecture, and reliable delivery over unnecessary complexity." },
  { title: "Long-Term Thinking", desc: "We think beyond launch and consider how products, systems, and businesses may need to evolve." },
];

const capabilityAreas = [
  "Software Engineering", "Product Development", "Web & Mobile", "Cloud & Infrastructure",
  "AI & Automation", "Data", "UI/UX", "Quality Engineering", "Technology Consulting",
  "Project & Delivery Management",
];

const cultureValues = [
  { title: "Curiosity", desc: "Ask why before deciding how." },
  { title: "Ownership", desc: "Take responsibility for the quality and outcome of the work." },
  { title: "Simplicity", desc: "Prefer clear solutions over unnecessary complexity." },
  { title: "Reliability", desc: "Do what you say, communicate early, and build systems people can depend on." },
  { title: "Continuous Learning", desc: "Technology changes constantly. Good teams keep learning." },
  { title: "Respect for the Problem", desc: "The best solution starts with understanding the real requirement." },
];

function TeamNetworkVisual() {
  return (
    <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-white/85 shadow-[0_40px_80px_-45px_rgba(11,18,32,0.35)] backdrop-blur">
      <div className="bg-grid-light absolute inset-0 opacity-60" aria-hidden="true" />
      <svg viewBox="0 0 480 340" className="relative block h-auto w-full" role="img" aria-label="Team capability network">
        <defs>
          <linearGradient id="team-net" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
        {[[240, 60], [370, 120], [370, 240], [240, 300], [110, 240], [110, 120]].map(([x, y], i, arr) => {
          const next = arr[(i + 1) % arr.length];
          return <line key={i} x1={x} y1={y} x2={next[0]} y2={next[1]} stroke="url(#team-net)" strokeWidth="1.2" opacity="0.45" className={i % 2 === 0 ? "dash-flow" : ""} />;
        })}
        {[[240, 60], [370, 120], [370, 240], [240, 300], [110, 240], [110, 120]].map(([x, y], i) => (
          <line key={`c${i}`} x1={x} y1={y} x2="240" y2="180" stroke="url(#team-net)" strokeWidth="1" opacity="0.3" />
        ))}
        <circle cx="240" cy="180" r="30" fill="#0B1220" />
        <text x="240" y="184" fontSize="10" fill="#fff" fontFamily="'Geist Mono', monospace" letterSpacing="2" textAnchor="middle">TEAM</text>
        {[[240, 60, "eng"], [370, 120, "product"], [370, 240, "cloud"], [240, 300, "data"], [110, 240, "design"], [110, 120, "ai"]].map(([x, y, l], i) => (
          <g key={l}>
            <circle cx={x} cy={y} r="7" fill="#fff" stroke="url(#team-net)" strokeWidth="2" />
            <text x={x} y={y + (y > 180 ? 24 : -16)} fontSize="9.5" fill="#94A3B8" fontFamily="'Geist Mono', monospace" letterSpacing="1.5" textAnchor="middle">{l}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function CollaborateVisual() {
  const center = { x: 240, y: 170 };
  const caps = [
    { label: "Product", x: 240, y: 42 },
    { label: "Engineering", x: 52, y: 120 },
    { label: "Design", x: 428, y: 120 },
    { label: "Cloud", x: 80, y: 292 },
    { label: "AI", x: 240, y: 312 },
    { label: "Consulting", x: 400, y: 292 },
  ];
  return (
    <div className="relative mx-auto max-w-[620px] overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_30px_60px_-40px_rgba(11,18,32,0.25)]">
      <div className="bg-grid-light absolute inset-0 opacity-50" aria-hidden="true" />
      <svg viewBox="0 0 480 340" className="relative block h-auto w-full" role="img" aria-label="Disciplines converging into one outcome">
        <defs>
          <linearGradient id="collab-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
        {caps.map((c, i) => (
          <line key={c.label} x1={c.x} y1={c.y} x2={center.x} y2={center.y} stroke="url(#collab-grad)" strokeWidth="1.2" opacity="0.5" className={i % 2 === 0 ? "dash-flow" : ""} />
        ))}
        <circle cx={center.x} cy={center.y} r="44" fill="#fff" stroke="url(#collab-grad)" strokeWidth="1.6" />
        <circle cx={center.x} cy={center.y} r="44" fill="none" stroke="url(#collab-grad)" strokeWidth="1.4" className="node-pulse" />
        <text x={center.x} y={center.y + 4} fontSize="10" fill="#0F172A" fontFamily="'Geist Mono', monospace" letterSpacing="2" textAnchor="middle">OUTCOME</text>
        {caps.map((c) => (
          <g key={c.label}>
            <circle cx={c.x} cy={c.y} r="5" fill="#fff" stroke="url(#collab-grad)" strokeWidth="1.8" />
            <text x={c.x} y={c.y + (c.y > center.y ? 24 : -14)} fontSize="10" fill="#64748B" fontFamily="'Geist Mono', monospace" letterSpacing="1" textAnchor="middle">
              {c.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function TeamPage() {
  return (
    <main className="bg-white" data-testid="team-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <Eyebrow>Our team</Eyebrow>
            </motion.div>
            <motion.h1
              data-testid="team-hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.95, ease: EASE, delay: 0.12 }}
              className="mt-6 text-[clamp(2.5rem,5vw,4.2rem)] font-extrabold leading-[1.03] tracking-[-0.03em] text-slate-900"
            >
              The people behind{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                Bytevora.
              </span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.28 }}
            >
              <p className="mt-6 max-w-[540px] text-base leading-relaxed text-slate-600 md:text-lg">
                We&rsquo;re building a team around engineering quality, product thinking, and the
                belief that good technology starts with understanding the problem.
              </p>
              <p className="mt-4 max-w-[540px] text-sm leading-relaxed text-slate-500 md:text-base">
                Our team structure is intentionally focused — bringing together technical thinking,
                business understanding, and a long-term approach to building software and digital products.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="team-work-with-us-button"
                  className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Work With Us
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  data-testid="team-about-link"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                >
                  About Bytevora
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            className="hidden lg:col-span-5 lg:block"
          >
            <TeamNetworkVisual />
          </motion.div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="team-leadership">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Leadership</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              Building Bytevora from the ground up.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Bytevora is led by its founding directors, with a focus on building a disciplined
              technology company capable of delivering client solutions today while developing
              long-term product and platform capabilities for the future.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {leaders.map((l, i) => (
              <Reveal key={l.name} delay={i}>
                <div
                  data-testid={`leader-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex h-full flex-col rounded-[20px] border border-slate-200 bg-white p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-blue-600/35 hover:shadow-[0_28px_55px_-32px_rgba(37,99,235,0.35)] md:p-10"
                >
                  <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100">
                    <img
                      src={l.photo}
                      alt={`${l.name}, ${l.role} at Bytevora`}
                      className="aspect-[4/4.4] w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="mt-7 flex items-baseline justify-between gap-4">
                    <h3 className="text-xl font-bold tracking-tight text-slate-900">{l.name}</h3>
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-blue-600">{l.role}</span>
                  </div>
                  <p className="mt-3.5 text-sm leading-[1.8] text-slate-600">{l.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="bg-white py-24 md:py-28" data-testid="team-how-we-work">
        <div className="mx-auto grid w-full max-w-[1200px] gap-14 px-6 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <Eyebrow>How we work</Eyebrow>
                <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
                  Small teams. Clear ownership. Better decisions.
                </h2>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-600 md:text-base">
                  We believe strong technology work does not depend on oversized teams. It depends
                  on clear thinking, the right expertise, and people who understand both the
                  technical and business context.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="lg:col-span-7">
            {workPrinciples.map((p, i) => (
              <Reveal key={p.title} delay={i} className={`group grid grid-cols-[64px_1fr] gap-6 border-t border-slate-200 py-8 md:grid-cols-[88px_1fr_1.1fr] md:px-4 ${i === workPrinciples.length - 1 ? "border-b" : ""}`}>
                <span className="pt-1 font-mono text-sm tracking-[0.16em] text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                  /0{i + 1}
                </span>
                <h3 className="text-lg font-semibold tracking-tight text-slate-900">{p.title}</h3>
                <p className="col-span-2 pl-[88px] text-sm leading-relaxed text-slate-600 md:col-span-1 md:pl-0 md:pt-0.5">
                  {p.desc}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team Philosophy */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="team-philosophy">
        <div className="mx-auto grid w-full max-w-[1200px] gap-10 px-6 md:px-8 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              We&rsquo;re building a team around capability, not hierarchy.
            </h2>
          </Reveal>
          <Reveal delay={1} className="flex flex-col justify-end gap-5">
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              As Bytevora grows, the team will expand around the needs of the products, clients,
              and technology we work with.
            </p>
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              We value people who can think independently, communicate clearly, take responsibility
              for outcomes, and understand that technology exists to solve real problems.
            </p>
          </Reveal>
          <Reveal delay={1} className="lg:col-span-2">
            <div className="flex flex-wrap gap-2.5 pt-4">
              {capabilityAreas.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 transition-colors duration-300 hover:border-blue-600/40 hover:text-blue-600"
                >
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Culture */}
      <section className="bg-white py-24 md:py-28" data-testid="team-culture">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-xl">
            <Eyebrow>Our culture</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              What matters to us.
            </h2>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-px overflow-hidden rounded-[20px] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3"
          >
            {cultureValues.map((v, i) => (
              <motion.div
                key={v.title}
                variants={fadeUp}
                data-testid={`culture-${i}`}
                className="group bg-white p-8 transition-colors duration-300 hover:bg-slate-50"
              >
                <span className="font-mono text-xs tracking-[0.16em] text-slate-300 transition-colors duration-300 group-hover:text-blue-600">
                  0{i + 1}
                </span>
                <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-slate-900">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Collaborate */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="team-collaborate">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Different disciplines, one outcome.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Modern technology projects rarely belong to a single discipline. A product may
              require software engineering, design, cloud infrastructure, automation, data,
              security, and business understanding working together. Bytevora&rsquo;s approach is
              to bring the right capabilities around the problem rather than separate work into
              rigid silos.
            </p>
          </Reveal>
          <Reveal delay={1} className="mt-14">
            <CollaborateVisual />
          </Reveal>
        </div>
      </section>

      {/* Growing With Purpose */}
      <section className="bg-white py-24 md:py-28" data-testid="team-growing">
        <div className="mx-auto grid w-full max-w-[1200px] gap-10 px-6 md:px-8 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Growing with purpose</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Building the team as the company grows.
            </h2>
          </Reveal>
          <Reveal delay={1} className="flex flex-col justify-end gap-5">
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Bytevora is at an early stage of its journey, and we are building the organization
              deliberately.
            </p>
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              As our work expands across software, cloud, AI, automation, product development, and
              consulting, we plan to grow the team around areas where deep capability can create
              meaningful value.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Careers */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="team-careers">
        <div className="mx-auto max-w-[860px] px-6 text-center md:px-8">
          <Reveal>
            <h2 className="text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-slate-900 md:text-[2.4rem]">
              Interested in building with us?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-[1.8] text-slate-600 md:text-base">
              We may not always have an open role listed, but we&rsquo;re interested in hearing
              from engineers, designers, product thinkers, and technology professionals who align
              with how we work.
            </p>
          </Reveal>
          <Reveal delay={1} className="mt-9">
            <Link
              to="/contact"
              data-testid="team-introduce-yourself-button"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
            >
              Introduce Yourself
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-32" data-testid="team-cta">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-blue-600/25 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-[860px] px-6 text-center md:px-8">
          <Reveal>
            <h2 className="text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white sm:text-5xl">
              Want to work with the team{" "}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                behind Bytevora?
              </span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Whether you have a product idea, a technical challenge, or simply want to explore
              how we could work together, start the conversation.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="team-cta-start-project"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-blue-600 hover:text-white"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="team-cta-contact"
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:text-blue-300"
            >
              Contact Bytevora
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
